/**
 * 이사 유형 인테이크 스펙/백엔드가 내려주는 타입명·포맷 힌트·JSON 접미사 등은 사용자에게 보이지 않게 필터링
 */

const RE_DATE_FORMAT_TOKEN = /yyyy\s*[-/.]\s*MM\s*[-/.]\s*dd/i;
const RE_PLACEHOLDER_DATE_RANGE = /^\d{4}-\d{2}-\d{2}\s*\/\s*\d{4}-\d{2}-\d{2}$/;
const RE_TECH_JSON_SUFFIX = /\b[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)*\s+JSON\s*$/i;
const RE_STRUCTURED_DEPARTURE = /structured\s*departure\s*address/i;
/** TIME 저장 스키마·개발용 힌트 (예: 시-분 JSON: {"hour":9,...}) */
const RE_TIME_JSON_HINT = /시[-–]?\s*분\s*JSON|"hour"\s*:\s*\d+\s*,\s*"minute"/i;

/** 설명/도움말로 쓰기 부적절한 문구면 null (렌더 생략) */
export function sanitizeIntakeHelpText(text: string | null | undefined): string | null {
  if (text == null) return null;
  const t = String(text).trim();
  if (t === '') return null;
  if (RE_STRUCTURED_DEPARTURE.test(t.replace(/\s/g, ''))) return null;
  if (RE_TECH_JSON_SUFFIX.test(t)) return null;
  if (RE_DATE_FORMAT_TOKEN.test(t)) return null;
  if (RE_PLACEHOLDER_DATE_RANGE.test(t.trim())) return null;
  if (RE_TIME_JSON_HINT.test(t)) return null;
  if (/\bJSON\b/i.test(t) && !/[가-힣]/.test(t)) return null;
  if (/\{[^}]*"hour"[^}]*"minute"[^}]*\}/.test(t)) return null;
  return t;
}

/** 필드 제목(name)이 타입 토큰·포맷 문자열이면 사용자용 기본 라벨로 대체 */
export function humanizeIntakeAttributeName(rawName: string, kindHint: string): string {
  let s = (rawName ?? '')
    .trim()
    .replace(/\s+JSON\s*$/i, '')
    .trim();
  if (s === '') return defaultLabelForKind(kindHint);

  if (RE_DATE_FORMAT_TOKEN.test(s) || RE_PLACEHOLDER_DATE_RANGE.test(s)) {
    return kindHint === 'DATE_RANGE' ? '기간' : '날짜';
  }
  if (RE_STRUCTURED_DEPARTURE.test(s.replace(/\s/g, ''))) {
    return '주소';
  }
  if (RE_TECH_JSON_SUFFIX.test(s)) {
    return defaultLabelForKind(kindHint);
  }
  if (kindHint === 'STRUCTURED_DEPARTURE_ADDRESS') {
    const compact = s.replace(/\s+/g, '');
    if (
      /structureddepartureaddress/i.test(compact) ||
      (/^[A-Za-z][A-Za-z0-9_]*$/.test(s) && !/[가-힣]/.test(s))
    ) {
      return '주소';
    }
  }
  if (kindHint === 'DATE_RANGE' && /^[A-Za-z0-9_\s\-/.]+$/.test(s) && !/[가-힣]/.test(s)) {
    return '기간';
  }

  return s;
}

function defaultLabelForKind(kind: string): string {
  switch (kind) {
    case 'STRUCTURED_DEPARTURE_ADDRESS':
      return '주소';
    case 'DATE_RANGE':
      return '기간';
    case 'TIME':
      return '시간';
    default:
      return '항목';
  }
}

/** 저장값이 포맷 플레이스홀더(yyyy-MM-dd 등)·빈 값이면 요약/완료 판정에서 제외 */
export function isPlaceholderDateLikeValue(raw: string | undefined): boolean {
  if (raw == null || String(raw).trim() === '') return true;
  const s = String(raw).trim();
  if (RE_DATE_FORMAT_TOKEN.test(s)) return true;
  if (/yyyy/i.test(s)) return true;
  return false;
}

const RE_ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * DATE_RANGE 저장 문자열 `start/end` — 양쪽이 yyyy-MM-dd이면 시작≤끝이 되도록 순서만 바꿈.
 * (역전이면 백엔드 타입 검증 실패)
 */
export function normalizeDateRangeSlashValue(raw: string): string {
  const s0 = String(raw ?? '').trim();
  if (!s0.includes('/')) return s0;
  const [a, b] = s0.split('/');
  const x = String(a ?? '').trim();
  const y = String(b ?? '').trim();
  if (!RE_ISO_DATE.test(x) || !RE_ISO_DATE.test(y)) return s0;
  if (x <= y) return `${x}/${y}`;
  return `${y}/${x}`;
}

/** DATE_RANGE: 슬래시 한 번, 양쪽 ISO, 시작일≤종료일 */
export function isWellFormedOrderedDateRangeSlash(raw: string): boolean {
  const s0 = String(raw ?? '').trim();
  if (!s0.includes('/')) return false;
  const [a, b] = s0.split('/');
  const x = String(a ?? '').trim();
  const y = String(b ?? '').trim();
  if (!RE_ISO_DATE.test(x) || !RE_ISO_DATE.test(y)) return false;
  return x <= y;
}
